/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Monday, November 29th 2021, 3:18:05 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { useAPIFetchItem, APISERVICEPATHS, useAPIForm } from "lib/api";
import {
  UserInvitation,
  UserInvitationValidationSchema,
  STAFF_USER_TYPES,
  InvitationAcceptance,
  InvitationAcceptanceValidationSchema,
} from "lib/schemas";
import React from "react";
import { useGlobalScope } from "core/providers";
import { URLS } from "core/routes";
import { getAbsoluteURL, getPathWithoutTrailingParams } from "core/routes/utils";

const workerInvitationsEndpoint = APISERVICEPATHS.workerInvitations;

const useAPIFetchInvitation = (invitation_token: string) => {
  const {
    apiItem: userInvitation,
    isMakingAPIRequest: isFetchingInvitation,
    isAPIError: invitationFetchHasError,
    httpResponseStatusCode: fetchInvitationStatusCode,
  } = useAPIFetchItem<UserInvitation>({
    apiServicePath: workerInvitationsEndpoint,
    apiPathParams: [invitation_token],
    makeRequestOnInitialMount: true,
  });

  return { userInvitation, isFetchingInvitation, invitationFetchHasError, fetchInvitationStatusCode };
};

const useInviteWorkersAPIForm = () => {
  const { globalState, userContext } = useGlobalScope();

  const { loggedInUser } = userContext;

  const defaultNewWorkerInvitation: UserInvitation = React.useMemo(
    () => ({
      invitee_emails: [],
      inviter: loggedInUser?.id,
      invited_as: STAFF_USER_TYPES.WORKER,
      organization: globalState.selectedOrganization?.id,
      department: null,
      portals: [],
      acceptance_base_url: `${getAbsoluteURL(getPathWithoutTrailingParams(URLS.workerAcceptInvitation))}`,
    }),
    [globalState.selectedOrganization?.id, loggedInUser?.id]
  );

  const [workerInvitation, setWorkerInvitation] = React.useState<UserInvitation>(defaultNewWorkerInvitation);

  const useAPIFormReturn = useAPIForm({
    apiInitialFetchId: null,
    primaryKey: null,
    apiFormDefaultValues: workerInvitation,
    apiFormDataValidationSchema: UserInvitationValidationSchema,
    apiServicePath: workerInvitationsEndpoint,
    setAPIFormResponse: setWorkerInvitation,
    successNotificationMessage: `Invitations successfully sent for ${globalState.selectedOrganization?.name}`,
  });

  React.useEffect(() => {
    useAPIFormReturn.useFormReturn.reset(defaultNewWorkerInvitation);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalState.selectedOrganization?.id, defaultNewWorkerInvitation]);

  return { useAPIFormReturn, workerInvitation };
};

const adminCompleteRegistrationEndpoint = APISERVICEPATHS.adminCompleteRegistration;

interface UseAdminCompleteRegistrationAPIFormOptions {
  invitation_token: string;
}

const useAdminCompleteRegistrationAPIForm = ({ invitation_token }: UseAdminCompleteRegistrationAPIFormOptions) => {
  const defaultInvitationAcceptanceValues: InvitationAcceptance = {
    invitation_token,
    first_name: "",
    last_name: "",
    password: "",
    confirm_password: "",
  };

  const useAPIFormReturn = useAPIForm<InvitationAcceptance>({
    apiInitialFetchId: null,
    primaryKey: "invitation_token",
    apiFormDefaultValues: defaultInvitationAcceptanceValues,
    apiFormDataValidationSchema: InvitationAcceptanceValidationSchema,
    apiServicePath: adminCompleteRegistrationEndpoint,
  });

  return {
    useAPIFormReturn,
  };
};

const workerAcceptInvitationEndpoint = APISERVICEPATHS.workerAcceptInvitation;

interface UseAcceptInvitationAPIFormOptions {
  invitation_token: string;
}

const useAcceptInvitationAPIForm = ({ invitation_token }: UseAcceptInvitationAPIFormOptions) => {
  const defaultInvitationAcceptanceValues: InvitationAcceptance = {
    invitation_token,
    first_name: "",
    last_name: "",
    password: "",
    confirm_password: "",
  };

  const useAPIFormReturn = useAPIForm<InvitationAcceptance>({
    apiInitialFetchId: null,
    primaryKey: "invitation_token",
    apiFormDefaultValues: defaultInvitationAcceptanceValues,
    apiFormDataValidationSchema: InvitationAcceptanceValidationSchema,
    apiServicePath: workerAcceptInvitationEndpoint,
  });

  return {
    useAPIFormReturn,
  };
};

export const useInvitationAPI = () => ({
  useAPIFetchInvitation,
  useInviteWorkersAPIForm,
  useAdminCompleteRegistrationAPIForm,
  useAcceptInvitationAPIForm,
});
